import React, { FC, useCallback, useEffect, useState } from 'react';
import { Feedback } from '../../../../config/api/models/Feedback';
import {
  DateColumn,
  DeleteButton,
  Description,
  FeedbackColumn,
  FeedbackReadOnlyWysiwyg,
  FeedbackRowWrapper,
  IconsWrapper,
  NewFeedbackLink,
  ReadIcon,
  RefinedTargetAudienceNotice,
} from './Atoms';
import { ConfirmationDialog, Icons } from '../../../shared/components';
import { TargetAudienceStatus } from './TargetAudienceStatus';
import { useTranslation } from 'react-i18next';
import { useFetch, useUser } from '../../../shared';
import apiRoutes from '../../../../config/api/routes';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import { useParams } from 'react-router';
import { UrlParams } from '../../../shared/types';
import { useTheme } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { IGroupStudent } from 'src/config/api/models/User';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';

type FeedbackRowProps = {
  data: Feedback;
  selectedGroupData?: IGroupStudent[];
  onUpdate: () => void;
};

export const FeedbackRow: FC<FeedbackRowProps> = ({ data, selectedGroupData, onUpdate }) => {
  const { t } = useTranslation();
  const { bookId, chapterId, trajectoryId } = useParams<UrlParams>();
  const { user, isStudent, isTeacher } = useUser();
  const theme = useTheme();
  const log = useDataLogging();
  const [hasRead, setHasRead] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFeedbackResponse, deleteFeedback, resetDeleteFeedback] = useFetch(
    apiRoutes.deleteFeedback(data.id),
    {
      method: 'DELETE',
    }
  );
  const [readFeedbackResponse, readFeedback, resetReadFeedback] = useFetch(
    apiRoutes.readFeedback(data.id),
    {
      method: 'PATCH',
    }
  );

  const handleDelete = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    setDeleting(true);
  }, []);

  const handleCloseConfirm = useCallback(
    (result: boolean) => {
      setDeleting(false);
      if (result) {
        log(LogActionTypes.FEEDBACK_TEACHER_DELETE, {
          bookId,
          chapterId,
          trajectoryId,
          feedbackId: data?.id,
        });
        deleteFeedback();
      }
    },
    [deleteFeedback, log, bookId, chapterId, trajectoryId, data]
  );

  useEffect(() => {
    if (deleteFeedbackResponse.success) {
      resetDeleteFeedback();
      onUpdate();
    }
  }, [deleteFeedbackResponse, resetDeleteFeedback, onUpdate]);

  useEffect(() => {
    if (readFeedbackResponse.success) {
      resetReadFeedback();
      onUpdate();
    }
  }, [readFeedbackResponse, resetReadFeedback, onUpdate]);

  const handleToggleRow = useCallback(() => {
    const newValue = !expanded;
    if (newValue && !hasRead) {
      log(isStudent ? LogActionTypes.FEEDBACK_STUDENT_READ : LogActionTypes.FEEDBACK_TEACHER_READ, {
        bookId,
        chapterId,
        trajectoryId,
        feedbackId: data?.id,
      });
      setHasRead(true);
    }
    setExpanded(newValue);
  }, [expanded, log, bookId, chapterId, trajectoryId, data, hasRead, isStudent]);

  return (
    <>
      <FeedbackRowWrapper onClick={handleToggleRow}>
        <DateColumn>{format(parseISO(data.createdAt), 'dd-MM-yyyy')}</DateColumn>
        <FeedbackColumn>
          <Description student={isStudent} expanded={expanded}>
            {isTeacher && !data.isWholeGroup && (
              <RefinedTargetAudienceNotice>
                {t('feedback.targetAudience.refinedNotice').replace(
                  '%NUM_STUDENTS%',
                  data.students.length.toString()
                )}
              </RefinedTargetAudienceNotice>
            )}
            {isStudent &&
            !data.students.find((student) => student.studentId === user.id)?.isRead ? (
              <NewFeedbackLink onClick={() => readFeedback()}>
                {t('feedback.readNewFeedback')}
              </NewFeedbackLink>
            ) : (
              <FeedbackReadOnlyWysiwyg
                expanded={expanded}
                id={data.id}
                value={data.description}
                readOnly
              />
            )}
          </Description>
          <IconsWrapper>
            {isTeacher && expanded && (
              <DeleteButton icon={Icons.DELETE} small onClick={handleDelete} />
            )}
            {isStudent &&
              data.students.find((student) => student.studentId === user.id)?.isRead && (
                <ReadIcon icon={Icons.READ} />
              )}
            <FontAwesomeSpriteIcon
              iconSize="lg"
              icon={expanded ? SpriteIconsLight.ANGLE_DOWN : SpriteIconsLight.ANGLE_UP}
              color={theme.palette.text.primary}
            />
          </IconsWrapper>
        </FeedbackColumn>
      </FeedbackRowWrapper>
      {isTeacher && expanded && (
        <TargetAudienceStatus
          selectedGroupData={selectedGroupData}
          feedbackStudents={data.students}
        />
      )}
      <ConfirmationDialog
        open={deleting}
        title={t('feedback.delete.title')}
        description={t('feedback.delete.description')}
        onClose={handleCloseConfirm}
      />
    </>
  );
};
